/**
 * Opting out of bulk WhatsApp sends.
 *
 * Meta requires marketing opt-outs to be honoured, and a broadcast that ignores
 * one is the fastest route to a quality drop and a paused number. The word the
 * message PROMISES and the word the system LISTENS FOR have to be the same, so
 * both live here: `OPT_OUT_FOOTER` is what the template editor inserts, and
 * `isOptOutKeyword` is what the inbound handler matches.
 *
 * This is deliberately separate from the blacklist. `whatsapp_blacklist` means
 * "the AI does not answer this person"; an opt-out means "no bulk sends" and
 * nothing else — they keep talking to the school, and the agent keeps replying.
 */
/**
 * The words that end bulk messaging, normalized (see `normalizeForOptOut`).
 *
 * Short on purpose. `BAJA` is the canonical Spanish one and the one the footer
 * promises; the rest are what people actually type anyway.
 */
export const OPT_OUT_KEYWORDS = [
    "BAJA",
    "DAR DE BAJA",
    "DARME DE BAJA",
    "STOP",
    "UNSUBSCRIBE",
    "CANCELAR SUSCRIPCION",
    "NO MAS MENSAJES",
    "NO QUIERO MAS MENSAJES",
];
// A bare "CANCELAR" is deliberately NOT in that list, and this is the kind of
// omission someone adds back for symmetry. In a school the word's commonest
// meaning is "cancel my visit" — it is a plausible reply to a quick-reply chip
// and to the visit flow. Treating it as an opt-out would do two wrong things at
// once: drop the person from every future send, and swallow the turn so nobody
// answers the cancellation they were actually asking for.
/** Upper-case, unaccented, punctuation-free, single-spaced. */
function normalizeForOptOut(text) {
    return text
        .normalize("NFD")
        // Strip the combining accents NFD just separated out, so "más" matches "MAS".
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .replace(/[^A-Z0-9 ]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
/**
 * Is this whole message an opt-out?
 *
 * EXACT match on the normalized text, never `includes`. "ya no quiero más
 * mensajes de ese programa" is a sentence inside a conversation, not a legal
 * opt-out, and matching loosely would silence campaigns for an engaged prospect
 * with nobody the wiser. The false negatives that leaves are covered by the two
 * other paths — Meta's own `131050` signal and the manual switch in the CRM —
 * both of which have a human or Meta in the loop.
 */
export function isOptOutKeyword(text) {
    const normalized = normalizeForOptOut(text);
    if (normalized === "")
        return false;
    return OPT_OUT_KEYWORDS.includes(normalized);
}
/**
 * The footer the template editor offers to insert on a MARKETING template.
 *
 * Meta fixes a template's text at approval, so nothing can be appended at send
 * time — authoring is the only point of leverage, which is why this is a
 * one-click button there rather than a rule enforced at send time.
 */
export const OPT_OUT_FOOTER = "Responde BAJA para dejar de recibir estos mensajes.";
/**
 * The automatic acknowledgement.
 *
 * Sent because Meta expects one and because silence reads as "it didn't work".
 * It always lands INSIDE the 24h service window by construction — they just
 * wrote to us — so it is legal free-form text and costs nothing.
 *
 * `{school}` is replaced with the client's name by the caller.
 */
export const OPT_OUT_ACK_TEMPLATE = "Listo, ya no recibirás mensajes masivos de {school}. Si necesitas algo, escríbenos cuando gustes.";
export function buildOptOutAck(schoolName) {
    return OPT_OUT_ACK_TEMPLATE.replace("{school}", schoolName);
}
/**
 * Does this template tell the recipient how to opt out?
 *
 * Used for a WARNING on a MARKETING broadcast, never a block: the wording is
 * free-form and a rule of ours rejecting a perfectly valid Spanish sentence
 * would be worse than the warning it replaces.
 */
export function mentionsOptOut(...parts) {
    const haystack = normalizeForOptOut(parts.filter(Boolean).join(" "));
    return OPT_OUT_KEYWORDS.some((word) => haystack.includes(word));
}
/** Where an opt-out came from. Stored so a complaint can be answered. */
export const OPT_OUT_SOURCES = ["keyword_reply", "meta_status", "manual"];
/**
 * Meta error codes that mean "this person does not want marketing".
 *
 * They arrive on the `statuses` webhook as a failed delivery. Treating them as a
 * plain failure and re-including the number in the next broadcast is exactly
 * what drives a number's quality rating down.
 */
export const META_OPT_OUT_ERROR_CODES = [131050, 131049];
export function isOptOutErrorCode(code) {
    return code !== null && code !== undefined && META_OPT_OUT_ERROR_CODES.includes(code);
}
//# sourceMappingURL=whatsapp-opt-out.js.map